import { useEffect, useState, useRef } from "react";
import { useAdBreakMute } from "../hooks/useAdBreakMute";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Calendar, ChevronLeft, ExternalLink, Share2, Tv, ArrowLeft, Users, Play, Clock, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { CustomVideoPlayer } from "./CustomVideoPlayer";
import YouTubePlayer from "./YouTubePlayer";
import { YouTubePushPlayer } from "./YouTubePushPlayer";
import { PushPointPlayer } from "./PushPointPlayer";
import { PlaylistPlayer } from "./PlaylistPlayer";
import { MatchupBanner } from "./MatchupBanner";
import { GameHub } from "./GameHub";

import { FanPoll } from "./FanPoll";
import { LiveStatsPanel } from "./LiveStatsPanel";
import { VideoAdPlayer } from "./VideoAdPlayer";
import { CHANNEL_LOGOS } from "../constants/channelLogos";
import { ASNLoader } from "./ASNLoader";
import { AdBreakOverlay } from "./AdBreakOverlay";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Game Selector Component - Shows when multiple games are available on the same channel/day
const GameSelector = ({ games, onSelectGame, channelName }) => {
  const formatTime = (time) => {
    if (!time) return 'TBD';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="relative aspect-video bg-gradient-to-br from-zinc-900 to-black overflow-hidden">
      <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase text-white mb-2">
            Select a Game
          </h2>
          <p className="text-zinc-400 text-sm">
            Multiple games are available on {channelName} today
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl">
          {games.map((game) => {
            const homeTeam = typeof game.home_team === 'object' ? game.home_team : { name: game.home_team };
            const awayTeam = typeof game.away_team === 'object' ? game.away_team : { name: game.away_team };
            
            return (
              <button
                key={game.id}
                onClick={() => onSelectGame(game)}
                className="bg-zinc-800/80 hover:bg-zinc-700/80 border border-white/10 hover:border-orange-500/50 p-4 transition-all group"
                data-testid={`game-selector-${game.id}`}
              >
                {/* Live Badge */}
                {game.is_live && (
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    <span className="text-red-500 text-xs font-bold uppercase">Live Now</span>
                  </div>
                )}
                
                {/* Teams */}
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {awayTeam?.logo_url ? (
                      <img src={awayTeam.logo_url} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{awayTeam?.name?.charAt(0)}</span>
                      </div>
                    )}
                    <span className="text-white text-sm font-medium truncate">
                      {awayTeam?.abbreviation || awayTeam?.name?.split(' ').pop()}
                    </span>
                  </div>
                  
                  <span className="text-zinc-500 text-xs">@</span>
                  
                  <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <span className="text-white text-sm font-medium truncate">
                      {homeTeam?.abbreviation || homeTeam?.name?.split(' ').pop()}
                    </span>
                    {homeTeam?.logo_url ? (
                      <img src={homeTeam.logo_url} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{homeTeam?.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Game Info */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime(game.scheduled_time)}
                  </span>
                  <span className="text-zinc-500 uppercase">{game.sport}</span>
                </div>
                
                {/* Watch Button */}
                <div className="mt-3 flex items-center justify-center gap-2 text-orange-500 group-hover:text-orange-400 text-sm font-bold uppercase">
                  <Play className="w-4 h-4" />
                  {game.is_live ? 'Watch Live' : 'Watch'}
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ChannelPage = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoReady, setVideoReady] = useState(false);
  const [creatingParty, setCreatingParty] = useState(false);
  
  // Multi-game selection state
  const [sameDayGames, setSameDayGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameSelector, setShowGameSelector] = useState(false);

  useEffect(() => {
    const fetchChannel = async () => {
      try {
        const response = await axios.get(`${API}/channels/${channelId}`);
        setChannel(response.data);
        
        // Fetch all broadcasts for this channel to check for same-day games
        const broadcastsRes = await axios.get(`${API}/broadcasts/schedule`);
        const allBroadcasts = broadcastsRes.data || [];
        
        // Get today's date
        const today = new Date().toISOString().split('T')[0];
        
        // Filter broadcasts for this channel on the same day
        const channelGamesToday = allBroadcasts.filter(b => 
          b.channel_id === channelId && 
          b.scheduled_date === today &&
          !b.is_completed
        );
        
        setSameDayGames(channelGamesToday);
        
        // If multiple games and no game selected yet, show selector
        if (channelGamesToday.length > 1 && !selectedGame) {
          setShowGameSelector(true);
        } else if (channelGamesToday.length === 1) {
          // Auto-select if only one game
          setSelectedGame(channelGamesToday[0]);
          setShowGameSelector(false);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchChannel();
    const interval = setInterval(fetchChannel, 15000);
    return () => clearInterval(interval);
  }, [channelId, selectedGame]);

  const handleSelectGame = (game) => {
    setSelectedGame(game);
    setShowGameSelector(false);
  };

  const handleBackToGameSelection = () => {
    setSelectedGame(null);
    setShowGameSelector(true);
  };

  const [adBreakActive, setAdBreakActive] = useState(false);
  const handleAdBreakChange = (active) => setAdBreakActive(active);
  const playerContainerRef = useRef(null);
  useAdBreakMute(playerContainerRef, adBreakActive);

  // Get live broadcast details including team info
  // Use selected game if available, otherwise fall back to channel's current broadcast
  const liveBroadcast = selectedGame || channel?.current_broadcast;
  const hasTeamMatchup = liveBroadcast?.home_team?.name && liveBroadcast?.away_team?.name;

  const formatDateTime = (date, time) => {
    const d = new Date(`${date}T${time}`);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) + ' at ' + 
           d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const startWatchParty = async () => {
    const token = localStorage.getItem("userToken");
    if (!token) {
      navigate(`/login-user?return=/watch/${channelId}`);
      return;
    }

    setCreatingParty(true);
    try {
      const res = await axios.post(`${API}/watch-party/create`, 
        { broadcast_id: liveBroadcast?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/watch-party/${res.data.party.code}`);
    } catch (err) {
      console.error("Error creating watch party:", err);
      alert(err.response?.data?.detail || "Failed to create watch party");
    } finally {
      setCreatingParty(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <ASNLoader size="fullscreen" />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Tv className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Channel Not Found</h2>
          <Link to="/channels" className="text-zinc-500 hover:text-white">← Back to Channels</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Channel Banner - shows below sticky header */}
      {channel.banner_url && (
        <div className="h-48 overflow-hidden">
          <img src={channel.banner_url} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      {/* Channel Info Section */}
      <div className={`bg-black/50 border-b border-white/10 ${!channel.banner_url ? 'pt-4' : ''}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Back Button */}
              <button
                onClick={() => navigate('/channels')}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="back-to-channels-btn"
                title="Back to Channels"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              {/* Channel Logo - use channel's own logo_url first, then fallback to hardcoded logos */}
              {(channel.logo_url || CHANNEL_LOGOS[channelId]) ? (
                <img 
                  src={channel.logo_url || CHANNEL_LOGOS[channelId]}
                  alt={channel.name}
                  className="h-16 w-auto"
                  style={{ background: 'transparent' }}
                />
              ) : (
                <div className="h-16 flex items-center">
                  <span className="font-secondary text-lg font-black uppercase tracking-tight text-white leading-tight max-w-[200px]">
                    {channel.abbreviation || channel.name}
                  </span>
                </div>
              )}
              <div>
                <h1 className="font-secondary text-2xl font-bold uppercase text-white">{channel.name}</h1>
                {channel.is_live && (
                  <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-red-500">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Live Now
                  </span>
                )}
              </div>
            </div>

            {/* Sponsor Section - Shows when enabled */}
            {channel.sponsor_enabled && channel.sponsor_logo && (
              <div className="flex items-center gap-3" data-testid="channel-sponsor">
                <span className="text-xs text-zinc-400 uppercase tracking-wider">Sponsored By</span>
                <img 
                  src={channel.sponsor_logo} 
                  alt={channel.sponsor_name || "Sponsor"} 
                  className="h-10 max-w-[140px] object-contain"
                  title={channel.sponsor_name}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Back to Game Selection Button - Shows when a game is selected and multiple games exist */}
        {selectedGame && sameDayGames.length > 1 && !showGameSelector && (
          <button
            onClick={handleBackToGameSelection}
            className="flex items-center gap-2 text-orange-500 hover:text-orange-400 text-sm font-bold uppercase mb-4 transition-colors"
            data-testid="back-to-game-selection-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Game Selection
          </button>
        )}

        {/* Video Player / Game Selector / Offline State */}
        <div ref={playerContainerRef} className="mb-0 relative">
          {/* Ad Break Overlay - only during live broadcasts */}
          {liveBroadcast?.id && (liveBroadcast.is_live || channel?.is_live) && <AdBreakOverlay broadcastId={liveBroadcast.id} onActiveChange={handleAdBreakChange} />}

          {/* Stream blocker during ad break — black layer blocks stream audio/interaction */}
          {adBreakActive && (liveBroadcast?.is_live || channel?.is_live) && (
            <div className="absolute inset-0 z-40 bg-black pointer-events-auto" data-testid="stream-blocker" />
          )}
          {/* Game Selector - Shows when multiple games on same channel/day */}
          {showGameSelector && sameDayGames.length > 1 ? (
            <GameSelector 
              games={sameDayGames} 
              onSelectGame={handleSelectGame}
              channelName={channel?.name || 'this channel'}
            />
          ) : channel.is_24_7_enabled && channel.playlist_items?.length > 0 && !channel.is_live ? (
            /* 24/7 Playlist Mode - Continuous playlist with live override */
            <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
              <PlaylistPlayer
                playlistItems={channel.playlist_items}
                currentIndex={channel.playlist_current_index || 0}
                shuffle={channel.playlist_shuffle}
                loop={channel.playlist_loop !== false}
                isLiveOverriding={channel.is_live}
                liveBroadcast={liveBroadcast}
                fallbackVideoUrl={channel.fallback_video_url}
                autoPlay={true}
                onIndexChange={(idx) => console.log("Playlist index:", idx)}
                scheduleEnabled={channel.schedule_enabled}
                scheduleItems={channel.schedule_items || []}
                fillGapsWithLoop={channel.fill_gaps_with_loop !== false}
              />
            </VideoAdPlayer>
          ) : channel.youtube_channel_id ? (
            /* YouTube Push Mode - Auto-embed YouTube channel live stream with fallback
               BUT: If selected game has its own YouTube URL, use that instead */
            selectedGame?.youtube_url ? (
              <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
                <YouTubePlayer
                  youtubeUrl={selectedGame.youtube_url}
                  isLive={true}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                />
              </VideoAdPlayer>
            ) : (
              <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
                <YouTubePushPlayer
                  youtubeChannelId={channel.youtube_channel_id}
                  fallbackVideoUrl={channel.fallback_video_url}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                  onLiveStatusChange={(isLive) => console.log("YouTube Push live status:", isLive)}
                />
              </VideoAdPlayer>
            )
          ) : channel.push_point_enabled ? (
            /* Push Point Mode - External M3U8 Stream
               BUT: If selected game has its own YouTube URL, use that instead */
            selectedGame?.youtube_url ? (
              <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
                <YouTubePlayer
                  youtubeUrl={selectedGame.youtube_url}
                  isLive={true}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                />
              </VideoAdPlayer>
            ) : (
              <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
                <PushPointPlayer
                  streamUrl={channel.push_point_url}
                  fallbackUrl={channel.fallback_video_url}
                  isLive={true}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                  onStreamStatus={(status) => console.log("Stream status:", status)}
                />
              </VideoAdPlayer>
            )
          ) : channel.is_live || (selectedGame && (selectedGame.youtube_url || selectedGame.vcloud_url)) ? (
            <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
              {/* Use VCloud Player when vcloud_url is set */}
              {liveBroadcast?.vcloud_url && liveBroadcast.vcloud_url.includes('/embed/') ? (
                <div className="aspect-video bg-black">
                  <iframe
                    src={liveBroadcast.vcloud_url}
                    title="Game Video - VCloud"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                </div>
              ) : liveBroadcast?.youtube_url ? (
                /* Use YouTube Player when youtube_url is set */
                <YouTubePlayer
                  youtubeUrl={liveBroadcast.youtube_url}
                  isLive={true}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                />
              ) : (
                <CustomVideoPlayer
                  channelId={channelId}
                  hlsUrl={channel.hls_url}
                  hlsQualities={channel.hls_qualities}
                  isLive={true}
                  autoPlay={true}
                  onReady={() => setVideoReady(true)}
                />
              )}
            </VideoAdPlayer>
          ) : channel.is_24_7_enabled && channel.playlist_items?.length > 0 ? (
            /* 24/7 Playlist Mode - Show even when not "live" but playlist is configured */
            <VideoAdPlayer channelId={channelId} showMidRollAfter={1500} midRollIntervalMin={1500} midRollIntervalMax={2700}>
              <PlaylistPlayer
                playlistItems={channel.playlist_items}
                currentIndex={channel.playlist_current_index || 0}
                shuffle={channel.playlist_shuffle}
                loop={channel.playlist_loop !== false}
                isLiveOverriding={false}
                fallbackVideoUrl={channel.fallback_video_url}
                autoPlay={true}
                scheduleEnabled={channel.schedule_enabled}
                scheduleItems={channel.schedule_items || []}
                fillGapsWithLoop={channel.fill_gaps_with_loop !== false}
              />
            </VideoAdPlayer>
          ) : (
            <div className="relative aspect-video bg-zinc-900 overflow-hidden">
              <img 
                src={channel.thumbnail_url} 
                alt={channel.name}
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative z-10 text-center">
                  <Radio className="w-20 h-20 text-zinc-600 mx-auto mb-6" />
                  <h2 className="font-secondary text-3xl font-black uppercase text-white mb-2">
                    Currently Offline
                  </h2>
                  <p className="text-zinc-500 mb-6">This channel is not currently broadcasting</p>
                  {channel.next_broadcast && (
                    <div className="inline-block bg-zinc-800/80 px-6 py-4">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Next Broadcast</div>
                      <div className="font-secondary text-lg font-bold text-white mb-1">{channel.next_broadcast.title}</div>
                      <div className="text-sm text-zinc-400">
                        {formatDateTime(channel.next_broadcast.scheduled_date, channel.next_broadcast.scheduled_time)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Matchup Banner - Shows when live with scheduled game */}
        {channel.is_live && hasTeamMatchup && (
          <MatchupBanner
            homeTeam={liveBroadcast.home_team}
            awayTeam={liveBroadcast.away_team}
          />
        )}

        {/* Game Hub - Live Poll, Player of the Game */}
        {channel.current_broadcast?.id && (
          <GameHub
            broadcast={channel.current_broadcast}
            isLive={channel.is_live}
            homeTeam={liveBroadcast?.home_team}
            awayTeam={liveBroadcast?.away_team}
            statsUrl={channel.current_broadcast?.stats_link}
          />
        )}

        {/* Live Stats Panel - Shows when broadcast has stats_link and is live */}
        {channel.is_live && channel.current_broadcast?.stats_link && (
          <div className="mt-4">
            <LiveStatsPanel 
              statsUrl={channel.current_broadcast.stats_link}
              isLive={channel.is_live}
              homeTeam={liveBroadcast?.home_team}
              awayTeam={liveBroadcast?.away_team}
            />
          </div>
        )}

        {/* Current Broadcast Info */}
        {channel.is_live && channel.current_broadcast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 p-6 mt-4 mb-8"
            data-testid="broadcast-info"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-2">
                  {channel.current_broadcast.sport}
                </span>
                <h2 className="font-secondary text-2xl font-black uppercase text-white mb-2">
                  {channel.current_broadcast.title}
                </h2>
                {channel.current_broadcast.description && (
                  <p className="text-zinc-400 mb-4">{channel.current_broadcast.description}</p>
                )}

                {/* External Links */}
                {channel.current_broadcast.external_links?.length > 0 && (
                  <div className="flex flex-wrap gap-3">
                    {channel.current_broadcast.external_links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 text-zinc-300 hover:text-white text-xs uppercase tracking-widest transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button 
                  onClick={startWatchParty}
                  disabled={creatingParty}
                  className="bg-zinc-800 text-white hover:bg-zinc-700 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  {creatingParty ? "Creating..." : "Watch Party"}
                </Button>
                <Button className="bg-white text-black hover:bg-zinc-200 font-secondary font-bold uppercase tracking-widest text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Upcoming Broadcasts */}
        {channel.upcoming_broadcasts?.length > 0 && (
          <div className="mb-8">
            <h3 className="font-secondary text-xl font-bold uppercase text-white mb-4">
              Upcoming on {channel.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channel.upcoming_broadcasts.filter(b => !b.is_live).slice(0, 6).map((broadcast) => (
                <div key={broadcast.id} className="bg-zinc-900/50 border border-white/5 p-4">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-2">
                    {broadcast.sport}
                  </span>
                  <h4 className="font-secondary text-white font-bold mb-2">{broadcast.title}</h4>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Calendar className="w-3 h-3" />
                    {formatDateTime(broadcast.scheduled_date, broadcast.scheduled_time)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Broadcasts */}
        {channel.past_broadcasts?.length > 0 && (
          <div>
            <h3 className="font-secondary text-xl font-bold uppercase text-white mb-4">
              Recent Broadcasts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {channel.past_broadcasts.slice(0, 6).map((broadcast) => (
                <div key={broadcast.id} className="bg-zinc-900/50 border border-white/5 p-4 opacity-60">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-2">
                    {broadcast.sport}
                  </span>
                  <h4 className="font-secondary text-white font-bold mb-2">{broadcast.title}</h4>
                  <div className="text-xs text-zinc-600">
                    {new Date(broadcast.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ChannelPage;
